import { useState, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import {
  Settings,
  DollarSign,
  Users,
  Plus,
  AlertCircle,
  Check,
  Save,
  Info,
  HelpCircle,
  MapPin,
  CalendarDays,
  Pencil,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ImportRatesFromLibraryDialog } from "@/components/ui/plans/ImportRatesFromLibraryDialog";
import { AGE_DETERMINATION_OPTIONS } from "@shared/lib/ageDetermination";
import { isLifeDisabilityType } from "@shared/schema";
import { TABLE_RATE_LABELS } from "@shared/constants/coverageTable";
import {
  AGE_BAND_PRESETS,
  AGE_BAND_TYPE_LABELS,
  parseAgeBandStart,
} from "@/lib/ageBands";
import { clientPlanEffectiveLabel } from "@/lib/clientPlanEffectiveBadge";

// Display labels for the locked Rating Method view - keep in sync with the
// SelectItem values in the "Convert Structure" unlocked Select below.
const RATING_METHOD_LABELS: Record<string, string> = {
  age_based: "Age-Based Rating",
  tier_rating: "Tier Rating",
  age_tier: "Age Band + Tier Rating",
  coverage_table: "Coverage Table",
};

// Maps the backend's plan_rates.rate_structure_type to the local
// ratingMethod values above, for comparing "what's saved" vs "what's shown".
const RATE_STRUCTURE_TYPE_TO_METHOD: Record<string, string> = {
  tier_rates: "tier_rating",
  age_rates: "age_based",
  wb_table: "age_based",
  age_tier_rates: "age_tier",
  table_rates: "coverage_table",
};

interface PlanRatesProps {
  planId: string;
  clientId?: string;
  planType?: string;
  /** Plan-year start (YYYY-MM-DD) of the client plan year being viewed; rates resolve the version for it. */
  planYearStart?: string;
}

export default function PlanRates({ planId, clientId, planType, planYearStart }: PlanRatesProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Read-only state comes from the SERVER's access rules (GET /api/plans/:id
  // embeds scope + permissions via computePlanAccess) — the UI never
  // re-derives ownership. Platform-scope plans are read-only for brokers;
  // agency-owned plans stay editable. Falls back to the ownership shape for
  // responses from an older server build.
  const { user: authUser } = useAuth();
  const { data: planRow } = useQuery<any>({
    queryKey: ["/api/plans", planId, "rates-access"],
    queryFn: async () => {
      const res = await fetch(`/api/plans/${planId}`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!planId,
  });
  const ratesReadOnly = Boolean(
    planRow &&
      (planRow.permissions
        ? planRow.permissions.canEditRates === false
        : planRow.clientId == null &&
          planRow.agencyId == null &&
          (authUser as any)?.role !== "superadmin"),
  );
  // Rating Method/Age Determination for platform-scope plans are now owned
  // by Superadmin's Plan Library - unlike ratesReadOnly above, this applies
  // to superadmin too, since no role should edit them from here anymore.
  const structureManagedInLibrary = Boolean(
    planRow && planRow.clientId == null && planRow.agencyId == null,
  );
  // Bug (DEV-310): ageDetermination was only ever set from a per-browser
  // localStorage cache (below) or the hardcoded default - never from
  // planRow, even though planRow (this same query) already carries the
  // real, authoritative plans.age_determination value. Two different
  // browsers/agencies could show two different values forever, and a
  // save from elsewhere never corrected what was on screen. This effect
  // is what makes the real value win once it's fetched, regardless of
  // whether the localStorage-load effect ran first or a stale cache
  // exists at all. ratingMethod/ageBandType don't need the same fix -
  // they're already correctly re-synced from planRatesData below (a
  // plan_rates value, not a plans column).
  useEffect(() => {
    if (planRow?.ageDetermination) setAgeDetermination(planRow.ageDetermination);
  }, [planRow?.ageDetermination]);

  const [importLibraryOpen, setImportLibraryOpen] = useState(false);

  // Rate state management
  const [ratingMethod, setRatingMethod] = useState<string>("age_based");
  // Rating Method starts locked (see the lockdown UI further down) - this
  // only flips true once the broker explicitly clicks "Convert Structure",
  // never on load, so it always resets to locked on a fresh page visit.
  const [structureUnlocked, setStructureUnlocked] = useState(false);
  const [ageBandType, setAgeBandType] = useState<string>("all_ages");
  const [ageBandRates, setAgeBandRates] = useState<Record<string, string>>({});
  // Bug: "Save Age-Based Rates" was always clickable, even with zero edits -
  // confusing, since it looks like there's something to save. Tracks the
  // last-known-saved values so the button can disable itself when the
  // current inputs match what's already persisted. Updated whenever
  // ageBandRates is set from an authoritative source (backend load, area
  // switch) and, via the ref below, after a successful save of exactly what
  // was actually sent (not a possibly-stale closure).
  const [savedAgeBandRates, setSavedAgeBandRates] = useState<Record<string, string>>({});
  const pendingSaveAgeBandRatesRef = useRef<Record<string, string> | null>(null);
  const [ageDetermination, setAgeDetermination] = useState<string>("plan_anniversary");
  const [tierRates, setTierRates] = useState({
    employee_only: "",
    employee_spouse: "",
    employee_children: "",
    family: "",
  });
  // When loaded from backend, this drives the table rendering
  const [backendAgeRows, setBackendAgeRows] = useState<
    Array<{ age: string; rate: string }>
  >([]);
  const [rateStructureType, setRateStructureType] = useState<string | null>(
    null
  );
  const [lockToBackendStructure, setLockToBackendStructure] =
    useState<boolean>(false);
  // Bug: "Age Band Options" and "Rate area" (and every other rating-method
  // card below) are gated on `ratingMethod`, which starts from whatever a
  // STALE localStorage save said (or the "age_based" default) and only gets
  // corrected once the backend fetch below finishes. On a slow network (or a
  // remount racing that fetch) the page renders with the wrong/default
  // ratingMethod first, so the card matching the REAL method - the one
  // holding both dropdowns - doesn't exist in the DOM yet. Reported in prod:
  // both dropdowns "disappear" together because they live in the same card.
  // structureLoaded blocks that flash: don't render any rating-method card
  // until we know the backend's answer (or that there isn't one).
  const [structureLoaded, setStructureLoaded] = useState<boolean>(false);
  const [origin, setOrigin] = useState<string | null>(null);
  // Age-tier matrix: ageBand -> { coverageTier -> rate }
  const [ageTierRates, setAgeTierRates] = useState<Record<string, Record<string, string>>>({});
  const [ageTierBandType, setAgeTierBandType] = useState<string>("five_year");

  const asOfParam = clientId && planYearStart ? `&asOf=${planYearStart}` : "";

  // Live plan rates from backend (used to render exact age bands provided)
  const { data: planRatesData } = useQuery({
    queryKey: clientId
      ? ["/api/plan-rates/", planId, clientId, planYearStart ?? null]
      : ["/api/plan-rates/", planId],
    queryFn: async () => {
      const url = clientId
        ? `/api/plan-rates/${planId}?clientId=${clientId}${asOfParam}`
        : `/api/plan-rates/${planId}`;
      const res = await fetch(url);
      if (!res.ok) return null;
      return res.json();
    },
  });
  // Multi-area plans return one row per (area, band). The viewer shows ONE
  // area at a time — mixing areas produced an unlabelled, arbitrary rate per
  // band (Loom: "I don't know which rate area it pulled in").
  const [viewAreaId, setViewAreaId] = useState<string>("");
  const rateAreas = useMemo(() => {
    const rates = (planRatesData?.rates as any[]) || [];
    const byId = new Map<string, string>();
    for (const r of rates) {
      if (r.ageBand == null || r.ratingAreaId == null) continue;
      const key = String(r.ratingAreaId);
      if (!byId.has(key)) byId.set(key, r.ratingAreaLabel || `Area ${r.ratingAreaId}`);
    }
    return Array.from(byId.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => Number(a.id) - Number(b.id));
  }, [planRatesData]);
  useEffect(() => {
    if (rateAreas.length === 0) {
      if (viewAreaId) setViewAreaId("");
      return;
    }
    if (!rateAreas.some((a) => a.id === viewAreaId)) {
      // Bug: this defaulted to rateAreas[0] (lowest ratingAreaId), which is
      // arbitrary and often NOT the area the client's plan link is actually
      // rated in - so the tab showed different rates than Plan Library for
      // "the same" area. resolvedRatingAreaId (added server-side in
      // GET /api/plan-rates/:planId) is the client's real rating area, via
      // the same resolveGroupRatingArea() lookup chargeService.ts uses to
      // compute actual premium charges - use it when present so this viewer
      // agrees with what the client is actually charged.
      const resolvedId =
        planRatesData?.resolvedRatingAreaId != null
          ? String(planRatesData.resolvedRatingAreaId)
          : null;
      const defaultId =
        resolvedId && rateAreas.some((a) => a.id === resolvedId)
          ? resolvedId
          : rateAreas[0].id;
      setViewAreaId(defaultId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateAreas.map((a) => a.id).join(","), planRatesData?.resolvedRatingAreaId]);
  const apiAgeRows = useMemo(() => {
    const rates = (planRatesData?.rates as any[]) || [];
    const rows = rates
      .filter((r: any) => r.ageBand != null)
      .filter((r: any) =>
        rateAreas.length === 0 ? true : String(r.ratingAreaId ?? "") === viewAreaId,
      )
      .map((r: any) => ({
        age: String(r.ageBand),
        rate: String(r.monthlyRate ?? ""),
      }))
      .sort((a: any, b: any) => parseStart(a.age) - parseStart(b.age));
    return rows;
  }, [planRatesData, rateAreas, viewAreaId]);
  // Bug: ageBandRates is the *edit buffer* actually rendered in the table
  // below (Input value={ageBandRates[age] ?? rate}) - but it's populated
  // once on mount from a separate, non-area-aware fetch (further down, the
  // "age_rates"/"wb_table" branch), which mixes every area's rows together
  // keyed only by age. Switching the "Rate area" dropdown recomputed
  // apiAgeRows correctly (it filters by viewAreaId) but never touched
  // ageBandRates, so the visible/editable values stayed stuck on whichever
  // area loaded first - Save then persisted the WRONG area's numbers too.
  // Re-seed the buffer from the area-filtered apiAgeRows whenever the
  // selected area changes, so what's shown/edited/saved always matches the
  // dropdown. Scoped to multi-area age-based plans only, and keyed off
  // viewAreaId (not apiAgeRows) so it doesn't clobber in-progress edits on
  // an unrelated background refetch.
  useEffect(() => {
    if (rateAreas.length === 0) return;
    if (ratingMethod !== "age_based") return;
    const next: Record<string, string> = {};
    for (const r of apiAgeRows) next[r.age] = r.rate;
    setAgeBandRates(next);
    setSavedAgeBandRates(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAreaId, rateAreas.length]);
  // Where these rates came from: version window of the rows on display.
  // Not limited to age-band rows - every structure (tier, table, age+tier)
  // resolves through the same version system, so any row on display carries
  // the same effectiveStart/effectiveEnd for this version.
  const rateSourceInfo = useMemo(() => {
    const rates = (planRatesData?.rates as any[]) || [];
    const row = rates.find(
      (r: any) => rateAreas.length === 0 || String(r.ratingAreaId ?? "") === viewAreaId,
    );
    if (!row) return null;
    const fmt = (d: any) => (d ? String(d).slice(0, 10) : null);
    const effectiveStart = fmt(row.effectiveStart);
    // `asOf` (from the server) is this client's own plan-year date - the one
    // used to pick which rate version to show. If the version actually shown
    // starts AFTER that date, the client's date came before every saved
    // version, and the resolver fell back to the earliest one on file rather
    // than the version that really applies to this client.
    const asOf = planRatesData?.asOf ? String(planRatesData.asOf).slice(0, 10) : null;
    const predatesEveryVersion = !!(asOf && effectiveStart && asOf < effectiveStart);
    return {
      effectiveStart,
      effectiveEnd: fmt(row.effectiveEnd),
      origin: row.origin ?? null,
      predatesEveryVersion,
    };
  }, [planRatesData, rateAreas, viewAreaId]);
  // Display-only label for the note above the table - never a control.
  // Prefers the per-row area (a labeled real area, e.g. "AREA 12") and
  // falls back to the client's resolved area when rates have no area
  // dimension at all.
  const currentRateAreaLabel = useMemo(() => {
    if (rateAreas.length > 0) {
      return rateAreas.find((a) => a.id === viewAreaId)?.label ?? rateAreas[0]?.label ?? null;
    }
    return planRatesData?.resolvedRatingAreaId != null ? String(planRatesData.resolvedRatingAreaId) : null;
  }, [rateAreas, viewAreaId, planRatesData?.resolvedRatingAreaId]);

  // DEV-365: on a client's plan the badge is the group's plan effective date
  // (client_plans.effective_date for the viewed plan year). The rate
  // version's date stays internal to rate resolution - never a warning here.
  const clientEffectiveLabel = clientId ? clientPlanEffectiveLabel(planYearStart) : null;

  // Which version's rates are on display - the effective date drives which
  // version resolves, so it should be visible for every rate structure, not
  // just age-based (that used to be the only one shown, since rateSourceInfo
  // used to only look at age-band rows).
  const rateSourceBadges = (currentRateAreaLabel || rateSourceInfo || clientEffectiveLabel) && (
    <div className="flex flex-wrap items-center gap-2">
      {currentRateAreaLabel && (
        <Badge variant="secondary" className="gap-1 font-normal">
          <MapPin className="h-3 w-3" />
          Rate Area {currentRateAreaLabel}
        </Badge>
      )}
      {clientId ? (
        clientEffectiveLabel && (
          <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
            <CalendarDays className="h-3 w-3" />
            Effective {clientEffectiveLabel}
          </Badge>
        )
      ) : rateSourceInfo?.effectiveStart ? (
        rateSourceInfo.predatesEveryVersion ? (
          // Same "Effective {date}" badge as the normal case below, but
          // amber + a tooltip instead of neutral, since this date is the
          // earliest version on file, not necessarily this client's real
          // rate (see tooltip copy).
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="gap-1 font-normal text-amber-700 border-amber-300 bg-amber-50 cursor-help"
              >
                <AlertCircle className="h-3 w-3" />
                Effective {rateSourceInfo.effectiveStart}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              This client's plan year starts before any saved rate version, so the
              earliest version on file is shown here instead - it may not be the rate
              this client should actually be charged. Verify with the carrier or add
              an earlier rate version.
            </TooltipContent>
          </Tooltip>
        ) : (
          <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
            <CalendarDays className="h-3 w-3" />
            Effective {rateSourceInfo.effectiveStart}
          </Badge>
        )
      ) : rateSourceInfo ? (
        // Legacy rows synced before rate versioning existed have no
        // effective date at all - warn instead of rendering them as if the
        // date were merely missing from display.
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className="gap-1 font-normal text-amber-700 border-amber-300 bg-amber-50 cursor-help"
            >
              <AlertCircle className="h-3 w-3" />
              No effective date on file
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            These rates were imported before rate versioning existed and were
            never assigned an effective date. Verify with the carrier before
            relying on them.
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );

  // Helpers
  function parseStart(label: string) {
    const start = label.split("-")[0].replace("+", "");
    const n = parseInt(start, 10);
    return isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
  }
  function normalizeDisplayLabel(label: string) {
    return label
      .toString()
      .replace(/^(\d+)\s*-\s*\1$/, "$1")
      .replace(/^(\d+)-99$/, "$1+");
  }
  const allAgesStandard = new Set([
    "0-14",
    ...Array.from({ length: 49 }, (_, i) => String(15 + i)),
    "64+",
  ]);
  const fiveYearStandard = new Set([
    "0-17",
    "18-24",
    "25-29",
    "30-34",
    "35-39",
    "40-44",
    "45-49",
    "50-54",
    "55-59",
    "60-64",
    "64+",
  ]);
  const tenYearStandard = new Set([
    "0-17",
    "18-29",
    "30-39",
    "40-49",
    "50-59",
    "60-64+",
  ]);
  const isStandardBand = (label: string) =>
    allAgesStandard.has(label) ||
    fiveYearStandard.has(label) ||
    tenYearStandard.has(label);
  // Bug: switching "Age Band Options" never changed the table - it only set
  // ageBandType, but the table's row-render (rowsToRender below) always
  // prefers apiAgeRows (whatever's already SAVED) whenever any exist, so the
  // dropdown had no path to override real data. Confirmed live: dropdown
  // said "All Ages" while the table showed 10-year-band labels, because the
  // saved rows never matched the selection. Per the user (history not
  // needed): switching just clears the table for a fresh fill-in under the
  // new structure. This gives the blank label set to switch TO.
  const bandLabelsFor = (type: string): string[] =>
    type === "five_year"
      ? Array.from(fiveYearStandard)
      : type === "ten_year"
        ? Array.from(tenYearStandard)
        : Array.from(allAgesStandard);
  const handleAgeBandTypeChange = (newType: string) => {
    setAgeBandType(newType);
    const blank: Record<string, string> = {};
    for (const label of bandLabelsFor(newType)) blank[label] = "";
    setAgeBandRates(blank);
    setSavedAgeBandRates(blank);
    setBackendAgeRows(
      bandLabelsFor(newType)
        .map((age) => ({ age, rate: "" }))
        .sort((a, b) => parseStart(a.age) - parseStart(b.age)),
    );
  };
  const inferLockFromAgeBands = (labels: string[]) => {
    if (!labels || labels.length === 0) return false;
    // Lock if any label is not part of our standard sets
    return labels.some((l) => !isStandardBand(l));
  };
  // Bug: "is this plan's structure backend-imported" only checked for the
  // literal string "wb_api", but plan_rates.origin is actually "wb_import"
  // for the vast majority of real plans (765k+ rows in prod vs. 16k
  // "wb_api") - plus "library_import"/"ease_migration"/"spec_seed" from
  // other loaders. Missing those left lockToBackendStructure false, so
  // "Age Band Options" rendered as a live, editable Select instead of the
  // "Structure locked" message - broker could change it, nothing happened,
  // because the table always renders the real imported rows (apiAgeRows)
  // regardless of that selector. Anything not explicitly "manual" (or
  // unset, for brand-new plans) is an import and should lock.
  const isImportedOrigin = (o: unknown) => {
    const v = String(o || "").toLowerCase();
    return v.length > 0 && v !== "manual";
  };

  // Initialize rates from localStorage or defaults
  useEffect(() => {
    if (!planId) return;

    // Load rates with client/prospect context
    const ratesStorageKey = `client_${
      clientId || "global"
    }_plan_${planId}_rates`;
    const savedRates = localStorage.getItem(ratesStorageKey);

    // Older builds shipped demo prefills (450/850/650/1050) and could persist
    // them to localStorage on save. If we see exactly that quartet on load,
    // treat it as if no rates were saved so the user gets empty inputs.
    const isLegacyDemoTierRates = (t: any) =>
      t &&
      t.employee_only === "450.00" &&
      t.employee_spouse === "850.00" &&
      t.employee_children === "650.00" &&
      t.family === "1050.00";

    if (savedRates) {
      const savedData = JSON.parse(savedRates);
      setAgeBandRates(savedData.ageBandRates || {});
      setTierRates(
        isLegacyDemoTierRates(savedData.tierRates)
          ? {
              employee_only: "",
              employee_spouse: "",
              employee_children: "",
              family: "",
            }
          : savedData.tierRates || {
              employee_only: "",
              employee_spouse: "",
              employee_children: "",
              family: "",
            }
      );
      setRatingMethod(savedData.ratingMethod || "age_based");
      setAgeBandType(savedData.ageBandType || "all_ages");
      if (savedData.ageDetermination) {
        setAgeDetermination(savedData.ageDetermination);
      }
      if (savedData.rateStructureType) {
        setRateStructureType(savedData.rateStructureType);
        setLockToBackendStructure(savedData.rateStructureType === "wb_table");
      }
      if (savedData.origin) {
        setOrigin(savedData.origin);
        if (isImportedOrigin(savedData.origin)) {
          setLockToBackendStructure(true);
        }
      }
      // Always populate backendAgeRows from saved age bands if available
      if (savedData.ageBandRates) {
        const labels = Object.keys(
          savedData.ageBandRates as Record<string, string>
        );
        if (labels.length > 0) {
          const rows = labels
            .map((age) => ({
              age,
              rate: String((savedData.ageBandRates as any)[age] ?? ""),
            }))
            .sort((a, b) => parseStart(a.age) - parseStart(b.age));
          setBackendAgeRows(rows);
          // If not already locked, infer lock from labels or origin
          const inferred = inferLockFromAgeBands(labels);
          const fromWB =
            isImportedOrigin(savedData.origin) ||
            savedData.rateStructureType === "wb_table";
          if (inferred || fromWB) setLockToBackendStructure(true);
        }
      }
    } else {
      // New plans start blank; user fills in rates manually.
      setAgeBandRates({});
    }
  }, [planId, clientId]);

  // Fetch persisted rates from backend and prefer them over localStorage/defaults
  useEffect(() => {
    if (!planId) {
      setStructureLoaded(true);
      return;
    }
    const url = clientId
      ? `/api/plan-rates/${planId}?clientId=${clientId}${asOfParam}`
      : `/api/plan-rates/${planId}`;
    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) return; // keep defaults if not found
        const json = await res.json();
        const structure = json?.rateStructure;
        const rates = json?.rates || [];
        if (!structure) return;

        if (structure.rateStructureType === "tier_rates") {
          setRatingMethod("tier_rating");
          // map planTierRates into state
          const next = { ...tierRates } as any;
          rates.forEach((r: any) => {
            next[r.coverageTier] =
              r.monthlyRate?.toString() || next[r.coverageTier];
          });
          setTierRates(next);
        } else if (
          structure.rateStructureType === "age_rates" ||
          structure.rateStructureType === "wb_table"
        ) {
          setRatingMethod("age_based");
          setAgeBandType(structure.ageBandType || "all_ages");
          setRateStructureType(structure.rateStructureType);
          // Bug: a multi-area plan's response carries EVERY area's rows in
          // one flat array. Iterating them unscoped mixed every area's
          // numbers together keyed only by age - whichever area's row
          // happened to land last in the (unordered) DB result silently won
          // for every age, regardless of which area the client actually
          // resolves to. Scope to the client's real resolved area first
          // (same one the "Rate Area" badge shows), falling back to
          // whichever area is present when there's no client context.
          const hasAreas = rates.some((r: any) => r.ratingAreaId != null);
          const resolvedAreaId = json?.resolvedRatingAreaId != null ? String(json.resolvedRatingAreaId) : null;
          const scopedRates = hasAreas
            ? rates.filter(
                (r: any) =>
                  String(r.ratingAreaId ?? "") ===
                  (resolvedAreaId ?? String(rates.find((x: any) => x.ratingAreaId != null)?.ratingAreaId ?? "")),
              )
            : rates;
          // Lock if backend marks wb_table OR inferred from labels
          const labelsFromApi = scopedRates
            .map((r: any) => (r.ageBand != null ? String(r.ageBand) : ""))
            .filter(Boolean);
          const inferredLock = inferLockFromAgeBands(labelsFromApi);
          const fromWB = scopedRates.some((r: any) => isImportedOrigin(r.origin));
          setOrigin(fromWB ? "wb_api" : "manual");
          setLockToBackendStructure(fromWB || inferredLock);
          const mapped: Record<string, string> = {};
          const rows: Array<{ age: string; rate: string }> = [];
          scopedRates.forEach((r: any) => {
            const age = r.ageBand?.toString() || "";
            const rate = r.monthlyRate?.toString() || "";
            if (age) {
              mapped[age] = rate;
              rows.push({ age, rate });
            }
          });
          if (rows.length > 0)
            setBackendAgeRows(
              rows.sort((a, b) => parseStart(a.age) - parseStart(b.age))
            );
          // Replace with backend-provided rates to avoid mixing defaults
          setAgeBandRates(mapped);
          setSavedAgeBandRates(mapped);
        } else if (structure.rateStructureType === "age_tier_rates") {
          setRatingMethod("age_tier");
          setAgeTierBandType(structure.ageBandType || "five_year");
          setRateStructureType("age_tier_rates");
          // Rebuild matrix from flat rows (each row has ageBand + coverageTier)
          const matrix: Record<string, Record<string, string>> = {};
          rates.forEach((r: any) => {
            const band = r.ageBand?.toString() || "";
            const tier = r.coverageTier?.toString() || "";
            const rate = r.monthlyRate?.toString() || "";
            if (band && tier) {
              if (!matrix[band]) matrix[band] = {};
              matrix[band][tier] = rate;
            }
          });
          setAgeTierRates(matrix);
        } else if (structure.rateStructureType === "table_rates") {
          setRatingMethod("coverage_table");
          const mapped: Record<string, string> = {};
          rates.forEach((r: any) => {
            // Coverage table rates are stored in coverageTable column
            const label = r.coverageTable?.toString() || "";
            const rate = r.monthlyRate?.toString() || "";
            if (label) {
              mapped[label] = rate;
            }
          });
          setAgeBandRates(mapped);
        }
      } catch (e) {
        // ignore fetch errors
      } finally {
        setStructureLoaded(true);
      }
    })();
  }, [planId, clientId, asOfParam]);

  // Removed plan-level dates from this tab; they are now managed in Overview

  // When age band type changes, build rows for editing
  useEffect(() => {
    if (ratingMethod !== "age_based") return;
    if (lockToBackendStructure) return;

    const buildFallbackBands = (
      type: string
    ): Array<{ age: string; rate: string }> => {
      if (type === "five_year") {
        const bands = [
          "0-17",
          "18-24",
          "25-29",
          "30-34",
          "35-39",
          "40-44",
          "45-49",
          "50-54",
          "55-59",
          "60-64",
          "64+",
        ];
        return bands.map((age) => ({ age, rate: ageBandRates[age] || "" }));
      }
      if (type === "ten_year") {
        const bands = ["0-17", "18-29", "30-39", "40-49", "50-59", "60-64+"];
        return bands.map((age) => ({ age, rate: ageBandRates[age] || "" }));
      }
      // all_ages default
      const ages: string[] = [
        "0-14",
        ...Array.from({ length: 49 }, (_, i) => String(15 + i)),
        "64+",
      ];
      return ages.map((age) => ({ age, rate: ageBandRates[age] || "" }));
    };

    (async () => {
      try {
        const res = await fetch(`/api/age-bands/${ageBandType}`);
        if (res.ok) {
          const bands = await res.json();
          if (Array.isArray(bands) && bands.length > 0) {
            const rows = bands.map((b: any) => ({
              age: b.ageBand as string,
              rate: ageBandRates[b.ageBand as string] || "",
            }));
            setBackendAgeRows(rows);
            return;
          }
        }
      } catch (_) {
        // ignore and use fallback
      }
      setBackendAgeRows(buildFallbackBands(ageBandType));
    })();
  }, [
    ageBandType,
    ratingMethod,
    lockToBackendStructure,
    backendAgeRows.length,
  ]);

  // Plan dates are no longer saved here

  const saveAgeBandMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("PUT", `/api/plan-age-rates/bulk/${planId}`, data);
    },
    onSuccess: () => {
      // Commit the values actually sent (not a possibly-stale closure over
      // ageBandRates) as the new "nothing to save" baseline.
      if (pendingSaveAgeBandRatesRef.current) {
        setSavedAgeBandRates(pendingSaveAgeBandRatesRef.current);
        pendingSaveAgeBandRatesRef.current = null;
      }
      // Clear rate-review flag (server clears it on save; this keeps UI in sync immediately)
      queryClient.setQueryData([`/api/plans/${planId}`], (prev: any) => {
        if (!prev || typeof prev !== "object") return prev;
        return { ...prev, needsRateReview: false };
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/plan-age-rates"],
        refetchType: "active",
      });
      // Keep plan-rates fresh for consumers like PerRateContributions
      queryClient.invalidateQueries({
        queryKey: ["/api/plan-rates/"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/plan-rates", planId],
        refetchType: "active",
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/plans"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/plans/${planId}`],
        refetchType: "active",
      });
      const clientIdNum = clientId ? Number(clientId) : NaN;
      if (Number.isFinite(clientIdNum)) {
        queryClient.invalidateQueries({
          queryKey: ["/api/client-plans", clientIdNum],
          refetchType: "active",
        });
      }
      // Notify listeners that rates were saved
      window.dispatchEvent(new CustomEvent("rates:saved"));
      toast({
        title: "Success",
        description: "Age-based rates saved successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save age-based rates",
        variant: "destructive",
      });
    },
  });

  const saveAgeDeterminationMutation = useMutation({
    mutationFn: async (data: { planId: number; ageDetermination: string }) => {
      return apiRequest("PATCH", `/api/plans/${data.planId}`, { ageDetermination: data.ageDetermination });
    },
    onSuccess: () => {
      // Bug (DEV-310): this invalidated ["/api/plans/"] (trailing slash),
      // which never matches planRow's real query key
      // (["/api/plans", planId, "rates-access"]) - so even with
      // ageDetermination now synced from planRow above, the screen
      // wouldn't reflect a successful save until a full reload. Also had
      // no success toast at all - the only way to tell it worked was
      // checking the network tab.
      queryClient.invalidateQueries({ queryKey: ["/api/plans", planId, "rates-access"] });
      queryClient.invalidateQueries({ queryKey: ["/api/plans/"] });
      queryClient.invalidateQueries({ queryKey: ["/api/aca/employee-line15-costs"] });
      toast({ title: "Success", description: "Age determination saved" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save age determination",
        variant: "destructive",
      });
    },
  });

  const saveTierRatesMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("PUT", `/api/plan-tier-rates/bulk/${planId}`, data);
    },
    onSuccess: () => {
      queryClient.setQueryData([`/api/plans/${planId}`], (prev: any) => {
        if (!prev || typeof prev !== "object") return prev;
        return { ...prev, needsRateReview: false };
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/plan-rates"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/plan-tier-rates"],
        refetchType: "active",
      });
      // Also invalidate keys used by PerRateContributions
      queryClient.invalidateQueries({
        queryKey: ["/api/plan-rates/"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/plan-rates", planId],
        refetchType: "active",
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/plans"],
        refetchType: "active",
      });
      queryClient.invalidateQueries({
        queryKey: [`/api/plans/${planId}`],
        refetchType: "active",
      });
      const clientIdNum = clientId ? Number(clientId) : NaN;
      if (Number.isFinite(clientIdNum)) {
        queryClient.invalidateQueries({
          queryKey: ["/api/client-plans", clientIdNum],
          refetchType: "active",
        });
      }
      // Notify listeners that rates were saved
      window.dispatchEvent(new CustomEvent("rates:saved"));
      toast({
        title: "Success",
        description: "Tier rates saved successfully",
        variant: "success",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save tier rates",
        variant: "destructive",
      });
    },
  });

  const saveRates = () => {
    if (!planId) return;

    // Require all four tier values > 0 before posting. Without this guard
    // empty / zero inputs would silently save a useless payload (server
    // currently accepts them).
    const requiredTiers: Array<keyof typeof tierRates> = [
      "employee_only",
      "employee_spouse",
      "employee_children",
      "family",
    ];
    const missing = requiredTiers.filter((k) => !(Number(tierRates[k]) > 0));
    if (missing.length > 0) {
      toast({
        title: "Enter all tier rates",
        description:
          "Each coverage tier needs a rate greater than $0 before saving.",
        variant: "destructive",
      });
      return;
    }

    // Save to localStorage for persistence
    const storageKey = `client_${clientId || "global"}_plan_${planId}_rates`;
    const dataToSave = {
      ageBandRates,
      tierRates,
      ratingMethod,
      ageBandType,
      ageDetermination,
      rateStructureType,
      origin,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(storageKey, JSON.stringify(dataToSave));

    // Save tier rates to database
    const tierRatesPayload = {
      planId: parseInt(planId),
      clientId: clientId ? parseInt(clientId) : null,
      ratingMethod,
      tierRates,
    };

    saveTierRatesMutation.mutate(tierRatesPayload);
  };

  const ageBandRatesDirty = useMemo(() => {
    const keys = new Set([...Object.keys(ageBandRates), ...Object.keys(savedAgeBandRates)]);
    for (const k of Array.from(keys)) {
      if ((ageBandRates[k] ?? "") !== (savedAgeBandRates[k] ?? "")) return true;
    }
    return false;
  }, [ageBandRates, savedAgeBandRates]);
  // Bug: "Save Age-Based Rates" stayed disabled after changing Age
  // Determination or Rating Method, even though handleSaveAgeBandRates DOES
  // persist both. Compare directly against what's already fetched instead
  // of adding more "last saved" state to track: planRatesData for the
  // structure/ageBandType, planRow for ageDetermination (a plain plans
  // column, not part of plan_rates). Falls back to the current value when
  // that data hasn't loaded yet, so it never false-positives on load.
  const savedRatingMethod = planRatesData?.rateStructure?.rateStructureType
    ? RATE_STRUCTURE_TYPE_TO_METHOD[planRatesData.rateStructure.rateStructureType] ?? ratingMethod
    : ratingMethod;
  const ageBandMetaDirty =
    ratingMethod !== savedRatingMethod ||
    ageBandType !== (planRatesData?.rateStructure?.ageBandType || "all_ages") ||
    ageDetermination !== (planRow?.ageDetermination || "plan_anniversary");
  // Guardrail: "Save Age-Based Rates" only ever writes ONE flat {age: rate}
  // map - whatever area the table happens to be showing - with no rating
  // area on the payload at all (handleSaveAgeBandRates below). For a
  // multi-area library plan that creates a client-specific override with
  // rating_area_id NULL, which then permanently shadows the real per-area
  // library rates for every area (GET /api/plan-rates/:planId prefers
  // client-specific rows). This silently destroyed 18 of 19 areas' pricing
  // for a real client plan (533/534, 2026-09-22) before anyone noticed.
  // Block saving here entirely for that exact shape; per-area correction is
  // "Import from Library" instead (see note above the table).
  const ageBandSaveBlocked = lockToBackendStructure && rateAreas.length > 1;

  const handleSaveAgeBandRates = () => {
    // Determine rows to persist: prefer backend; if locked but empty, derive from ageBandRates
    const sourceRows =
      backendAgeRows.length > 0
        ? backendAgeRows
        : lockToBackendStructure
        ? Object.keys(ageBandRates)
            .map((age) => ({ age, rate: ageBandRates[age] || "" }))
            .sort((a, b) => parseStart(a.age) - parseStart(b.age))
        : [];
    const filteredAgeBandRates: Record<string, string> = {};
    sourceRows.forEach(({ age, rate }) => {
      filteredAgeBandRates[age] = ageBandRates[age] ?? rate ?? "";
    });

    const ageBandPayload = {
      planId: parseInt(planId!),
      clientId: clientId ? parseInt(clientId) : null,
      ageBandType,
      ratingMethod,
      ageBandRates: filteredAgeBandRates,
      origin: lockToBackendStructure ? "wb_api" : "manual",
    };

    // Also save to localStorage for persistence
    const storageKey = `client_${clientId || "global"}_plan_${planId}_rates`;
    const dataToSave = {
      ageBandRates: filteredAgeBandRates,
      tierRates,
      ratingMethod,
      ageBandType,
      ageDetermination,
      rateStructureType: "age_rates",
      origin: lockToBackendStructure ? "wb_api" : "manual",
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(storageKey, JSON.stringify(dataToSave));

    // Bug: this called saveAgeBandMutation unconditionally, even for an
    // Age Determination-only change with zero rate edits. That PUT has no
    // effectiveStart, so on a plan that already has a published rate
    // version, the backend's RATE_VERSION_REQUIRED guardrail rejects it
    // outright (planRatesRoutes.ts ~line 648) - a real rate-value save
    // would legitimately need to provide a date there, but this wasn't one.
    // Only hit that endpoint when there's an actual rate value or structure
    // change to persist; Age Determination always saves independently via
    // its own PATCH regardless.
    const structureChanged =
      ageBandType !== (planRatesData?.rateStructure?.ageBandType || "all_ages") ||
      ratingMethod !== savedRatingMethod;
    if (ageBandRatesDirty || structureChanged) {
      pendingSaveAgeBandRatesRef.current = filteredAgeBandRates;
      saveAgeBandMutation.mutate(ageBandPayload);
    }

    // Also save age determination to the plan
    saveAgeDeterminationMutation.mutate({ planId: parseInt(planId), ageDetermination });
  };

  // Save age-tier (matrix) rates
  const saveAgeTierMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("PUT", `/api/plan-age-tier-rates/bulk/${planId}`, data);
    },
    onSuccess: () => {
      queryClient.setQueryData([`/api/plans/${planId}`], (prev: any) => {
        if (!prev || typeof prev !== "object") return prev;
        return { ...prev, needsRateReview: false };
      });
      queryClient.invalidateQueries({ queryKey: ["/api/plan-rates/"], refetchType: "active" });
      queryClient.invalidateQueries({ queryKey: ["/api/plan-rates", planId], refetchType: "active" });
      queryClient.invalidateQueries({ queryKey: ["/api/plans"], refetchType: "active" });
      queryClient.invalidateQueries({ queryKey: [`/api/plans/${planId}`], refetchType: "active" });
      const clientIdNum = clientId ? Number(clientId) : NaN;
      if (Number.isFinite(clientIdNum)) {
        queryClient.invalidateQueries({ queryKey: ["/api/client-plans", clientIdNum], refetchType: "active" });
      }
      window.dispatchEvent(new CustomEvent("rates:saved"));
      toast({ title: "Success", description: "Age band + tier rates saved successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to save age-tier rates", variant: "destructive" });
    },
  });

  const handleSaveAgeTierRates = () => {
    if (!planId) return;
    saveAgeTierMutation.mutate({
      clientId: clientId ? parseInt(clientId) : null,
      ageBandType: ageTierBandType,
      ageTierRates,
    });
    saveAgeDeterminationMutation.mutate({ planId: parseInt(planId), ageDetermination });
  };

  // Age bands for the age-tier matrix
  const ageTierBands = useMemo(() => {
    const preset =
      ageTierBandType === "ten_year"
        ? AGE_BAND_PRESETS.ten_year
        : AGE_BAND_PRESETS.five_year;
    let bands: string[] = [...preset];
    // Merge in any extra bands from backend that aren't in the standard set
    const bandSet = new Set(bands);
    const extraBands = Object.keys(ageTierRates).filter((b) => !bandSet.has(b));
    if (extraBands.length > 0) {
      bands = [...bands, ...extraBands].sort(
        (a, b) => parseAgeBandStart(a) - parseAgeBandStart(b),
      );
    }
    return bands;
  }, [ageTierBandType, ageTierRates]);

  const COVERAGE_TIERS = [
    { key: "employee_only", label: "EE Only" },
    { key: "employee_spouse", label: "EE+Spouse" },
    { key: "employee_children", label: "EE+Child" },
    { key: "family", label: "Family" },
  ] as const;

  const handleImportFromLibrary = (result: {
    ageBandRates: Record<string, string>;
    ratingAreaId?: number;
    ratingAreaLabel?: string;
    effectiveStart?: string;
  }) => {
    // Apply to local UI immediately
    setRatingMethod("age_based");
    setAgeBandType("all_ages");
    setRateStructureType("age_rates");
    setOrigin("wb_api");
    setLockToBackendStructure(true);

    const rows = Object.keys(result.ageBandRates)
      .map((age) => ({ age, rate: result.ageBandRates[age] || "" }))
      .sort((a, b) => parseStart(a.age) - parseStart(b.age));
    setBackendAgeRows(rows);
    setAgeBandRates(result.ageBandRates);

    // Persist to backend. WHY effectiveStart matters here: PUT
    // /api/plan-age-rates/bulk/:planId (planRatesRoutes.ts) only scopes its
    // replace to ONE rating area when both ratingAreaId AND effectiveStart
    // are present (its versionScope branch) - omitting the date falls back
    // to replacing every area for this client, which is the bug this
    // session found and fixed the read side for
    // (docs/RATES_TAB_BUGS_FOUND.md). GET /api/plan-rates/:planId now merges
    // per area, so this write only needs to stay correctly scoped; it
    // doesn't need to touch or know about the plan's OTHER areas.
    const payload = {
      clientId: clientId ? parseInt(clientId) : null,
      ageBandType: "all_ages",
      ratingMethod: "age_table",
      ageBandRates: result.ageBandRates,
      ratingAreaId: result.ratingAreaId,
      ratingAreaLabel: result.ratingAreaLabel,
      effectiveStart: result.effectiveStart,
      origin: "wb_api",
    };

    // Also save to localStorage for persistence
    const storageKey = `client_${clientId || "global"}_plan_${planId}_rates`;
    const dataToSave = {
      ageBandRates: result.ageBandRates,
      tierRates,
      ratingMethod: "age_based",
      ageBandType: "all_ages",
      rateStructureType: "age_rates",
      origin: "wb_api",
      ratingAreaId: result.ratingAreaId ?? null,
      ratingAreaLabel: result.ratingAreaLabel ?? null,
      savedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    } catch {}

    saveAgeBandMutation.mutate(payload);
  };

  const isLifeDisability = isLifeDisabilityType(planType);

  return (
    <div className="space-y-6">
      {ratesReadOnly && (authUser as any)?.role !== "employer" && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
          <span className="font-medium">Platform-managed rates.</span> This plan comes from the
          shared Plan Library — its rates are maintained centrally and apply to every agency.
          You can review them here, but changes are made by the platform administrator.
        </div>
      )}
      {structureManagedInLibrary && (authUser as any)?.role !== "employer" && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
          As entered in the Plan Library. This plan is managed by the platform, so its Rating
          Method and Age Determination are edited there.
        </div>
      )}
      {!isLifeDisability && (
      <Card>
        <CardHeader>
          {/* WHY the pencil lives here, not next to Rating Method: it
              unlocks the WHOLE card (Rating Method, Age Determination, and
              every rate input/Save button below via structureUnlocked) -
              putting it next to just one field made it look like it only
              applied there, and squeezed that field's column against Age
              Determination's next to it in the 2-col grid. One icon for
              "edit this section," in the header, same pattern as a
              standard edit-pencil affordance. */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Rate Structure Configuration
              </CardTitle>
              <CardDescription>
                Configure rating method and coverage tiers for this plan
              </CardDescription>
            </div>
            {!structureUnlocked && !structureManagedInLibrary && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      disabled={ratesReadOnly}
                      onClick={() => setStructureUnlocked(true)}
                    >
                      <Pencil className="h-4 w-4" />
                      <span className="sr-only">Edit rate structure</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Convert Structure</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              {/* Same fixed-height row as the Age Determination label (which
                  carries a help icon) so the two selects align. */}
              <div className="flex h-5 items-center">
                <Label className="font-medium">Rating Method</Label>
              </div>
              {/* Bug: this was a plain live Select with no check against
                  what's actually saved for the plan. Nothing stopped
                  someone from picking a method that didn't match the real
                  stored rates, then saving on that mismatched card - which
                  writes a whole SECOND rate structure (e.g. age_rates)
                  alongside the plan's real one (e.g. age_tier_rates), with
                  no rule for which one is "correct" afterward (that's what
                  the merge-order fix above addresses, but it's a
                  tie-breaker, not a fix for creating the mess in the first
                  place). Locked by default to what's actually saved;
                  "Convert Structure" is now a deliberate, one-way opt-in to
                  changing it, so it can't happen by an idle dropdown click. */}
              {!structureUnlocked ? (
                <div className="flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm">
                  {RATING_METHOD_LABELS[ratingMethod] || ratingMethod}
                </div>
              ) : (
                <Select value={ratingMethod} onValueChange={setRatingMethod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select rating method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="age_based">Age-Based Rating</SelectItem>
                    <SelectItem value="tier_rating">Tier Rating</SelectItem>
                    <SelectItem value="age_tier">Age Band + Tier Rating</SelectItem>
                    <SelectItem value="coverage_table">Coverage Table</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            {(ratingMethod === "age_based" || ratingMethod === "age_tier") && (
              <div className="space-y-2">
                <div className="flex h-5 items-center gap-2">
                  <Label className="font-medium">Age Determination</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm p-3">
                        <p className="text-sm">
                          <strong>Age Determination</strong> defines the date used to calculate 
                          each person's "rating age" for premium calculation.
                        </p>
                        <ul className="mt-2 text-xs space-y-1">
                          <li><strong>Plan Anniversary:</strong> Uses plan anniversary date, except mid-year joiners use their coverage start date.</li>
                          <li><strong>Plan Anniversary (Ignore Hire):</strong> Always uses plan anniversary, even for mid-year joiners.</li>
                          <li><strong>First of Month:</strong> Age as of the 1st of each coverage month.</li>
                          <li><strong>End of Month:</strong> Age as of the last day of each coverage month.</li>
                          <li><strong>First of Year:</strong> Age locked as of January 1st.</li>
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Select
                  value={ageDetermination}
                  onValueChange={setAgeDetermination}
                  disabled={ratesReadOnly || !structureUnlocked}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select age determination" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_DETERMINATION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Plan-level dates have been moved to Overview */}
        </CardContent>
      </Card>
      )}

      {/* Bug (see structureLoaded above): every card below picks which
          fields to show based on `ratingMethod`, which is wrong/default
          until the backend fetch resolves. Rendering them before that
          finishes was how "Age Band Options" and "Rate area" could both
          vanish together - they're both inside the one card matching the
          CORRECT ratingMethod, which doesn't exist in the DOM yet. */}
      {!structureLoaded ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Loading rate configuration…
          </CardContent>
        </Card>
      ) : (
      <>
      {!isLifeDisability && ratingMethod === "tier_rating" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Coverage Tier Rates
            </CardTitle>
            <CardDescription>
              Define rates for each coverage tier
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {rateSourceBadges}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-muted-foreground">
                  4 coverage tiers available
                </span>
              </div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Tier Rate
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Add Coverage Tier Rate</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Coverage Tier</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Select coverage tier" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="employee_only">
                            Employee Only
                          </SelectItem>
                          <SelectItem value="employee_spouse">
                            Employee + Spouse
                          </SelectItem>
                          <SelectItem value="employee_child">
                            Employee + Child(ren)
                          </SelectItem>
                          <SelectItem value="family">Family</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Monthly Premium</Label>
                      <Input placeholder="450.00" />
                    </div>
                    <div className="space-y-2">
                      <Label>Employee Contribution</Label>
                      <Input placeholder="135.00" />
                    </div>
                    <div className="space-y-2">
                      <Label>Employer Contribution</Label>
                      <Input placeholder="315.00" />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline">Cancel</Button>
                      <Button>Add Rate</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              <div className="grid gap-3">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">Employee Only</div>
                      <div className="text-sm text-muted-foreground">
                        Single coverage
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <span className="text-lg font-bold">$</span>
                      <Input
                              disabled={ratesReadOnly || !structureUnlocked}
                        type="number"
                        value={tierRates.employee_only}
                        onChange={(e) =>
                          setTierRates((prev) => ({
                            ...prev,
                            employee_only: e.target.value,
                          }))
                        }
                        step="0.01"
                        placeholder="0.00"
                        className="w-24 text-lg font-bold"
                      />
                      <div className="text-xs text-muted-foreground">
                        per month
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">Employee + Spouse</div>
                      <div className="text-sm text-muted-foreground">
                        Two-party coverage
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <span className="text-lg font-bold">$</span>
                      <Input
                              disabled={ratesReadOnly || !structureUnlocked}
                        type="number"
                        value={tierRates.employee_spouse}
                        onChange={(e) =>
                          setTierRates((prev) => ({
                            ...prev,
                            employee_spouse: e.target.value,
                          }))
                        }
                        step="0.01"
                        placeholder="0.00"
                        className="w-24 text-lg font-bold"
                      />
                      <div className="text-xs text-muted-foreground">
                        per month
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">Employee + Child(ren)</div>
                      <div className="text-sm text-muted-foreground">
                        Employee plus dependents
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <span className="text-lg font-bold">$</span>
                      <Input
                              disabled={ratesReadOnly || !structureUnlocked}
                        type="number"
                        value={tierRates.employee_children}
                        onChange={(e) =>
                          setTierRates((prev) => ({
                            ...prev,
                            employee_children: e.target.value,
                          }))
                        }
                        step="0.01"
                        placeholder="0.00"
                        className="w-24 text-lg font-bold"
                      />
                      <div className="text-xs text-muted-foreground">
                        per month
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">Family</div>
                      <div className="text-sm text-muted-foreground">
                        Full family coverage
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <span className="text-lg font-bold">$</span>
                      <Input
                              disabled={ratesReadOnly || !structureUnlocked}
                        type="number"
                        value={tierRates.family}
                        onChange={(e) =>
                          setTierRates((prev) => ({
                            ...prev,
                            family: e.target.value,
                          }))
                        }
                        step="0.01"
                        placeholder="0.00"
                        className="w-24 text-lg font-bold"
                      />
                      <div className="text-xs text-muted-foreground">
                        per month
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-6">
              <Button
                onClick={saveRates}
                disabled={ratesReadOnly || saveTierRatesMutation.isPending || !structureUnlocked}
                className="flex items-center gap-2"
              >
                <Check className="h-4 w-4" />
                {saveTierRatesMutation.isPending
                  ? "Saving..."
                  : "Save All Rates"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLifeDisability && ratingMethod === "age_based" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Age Band Configuration
            </CardTitle>
            <CardDescription>
              Select age band structure for age-based rating
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-end">
              {/* WHY not gated by ageBandSaveBlocked: this button now sends
                  ratingAreaId + effectiveStart (ImportRatesFromLibraryDialog
                  -> handleImportFromLibrary above), which the backend scopes
                  to exactly that one area (planRatesRoutes.ts versionScope),
                  and GET /api/plan-rates/:planId now merges per area - so
                  importing one area here no longer touches the others.
                  Manual typing below could send the same viewAreaId +
                  rateSourceInfo.effectiveStart and be just as safe - it's
                  blocked because joshuas's direction is that rate/area changes
                  go through this button specifically, not because of a
                  remaining technical gap. */}
              {/* WHY the label switches: per Joshua, once a plan's rates are
                  already imported (locked, lockToBackendStructure), this
                  button is how you fix a wrong plan/area - not how you add
                  rates for the first time. "Update rates" says that; "Import
                  from library" would read as "there's nothing here yet",
                  which is wrong once it's locked. */}
              <Button
                variant="outline"
                onClick={() => setImportLibraryOpen(true)}
              >
                {lockToBackendStructure ? "Update rates" : "Import from library"}
              </Button>
            </div>

            {/* WHY these props: the broker was retyping state/carrier/plan/
                date they were already looking at every time they opened this
                dialog - which is exactly how someone entered a DIFFERENT
                effective date than the one actually showing and hit the
                cache-miss bug above. Pre-fill from what's already loaded on
                this page instead. planId/carrierId are OUR internal ids,
                matched directly by the dialog against what
                /api/plan-source/{carriers,plans} already return (both
                expose our ids, see planSourceRoutes.ts). effectiveDate
                intentionally comes from rateSourceInfo (the currently
                DISPLAYED rate version's effective date), not planRow's own
                effectiveDate field - those can differ once a plan has been
                renewed/versioned, and the displayed date is what the broker
                sees and expects to reuse. */}
            <ImportRatesFromLibraryDialog
              open={importLibraryOpen}
              onOpenChange={setImportLibraryOpen}
              onImport={handleImportFromLibrary}
              initialState={planRow?.availableStates?.[0]}
              initialEffectiveDate={rateSourceInfo?.effectiveStart ?? undefined}
              initialCarrierId={planRow?.carrierId}
              initialPlanId={planId ? parseInt(planId) : undefined}
              initialRatingAreaId={viewAreaId ? Number(viewAreaId) : undefined}
            />

            <div className="space-y-4">
              {/* Bug: this info line rendered unconditionally, so a locked
                  plan showed "Choose an age band structure..." right above
                  "Structure locked" - two contradictory messages at once.
                  Only show it alongside the actual dropdown. */}
              {lockToBackendStructure ? (
                <div className="text-sm text-muted-foreground">
                  Using the plan's imported age-band structure. Structure
                  locked.
                </div>
              ) : !structureUnlocked ? (
                // Same lockdown as Rating Method above: read-only until
                // "Convert Structure" is clicked, for the same reason -
                // this shouldn't be changeable by an idle dropdown click.
                <div className="space-y-2">
                  <Label className="font-medium">Age Band Options</Label>
                  <div className="flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm">
                    {AGE_BAND_TYPE_LABELS[ageBandType as keyof typeof AGE_BAND_TYPE_LABELS] ||
                      (ageBandType === "all_ages" ? "All Ages" : ageBandType)}
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-muted-foreground">
                      Choose an age band structure for rate calculations
                    </span>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium">Age Band Options</Label>
                    <Select value={ageBandType} onValueChange={handleAgeBandTypeChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select age band structure" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_ages">All Ages</SelectItem>
                        <SelectItem value="five_year">
                          5 Year Age Bands
                        </SelectItem>
                        <SelectItem value="ten_year">
                          10 Year Age Bands
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>

            <div className="space-y-3">
              {/* Product correction: the rate area picker looked like a live
                  control but changing it never changed what's actually
                  charged (that's fixed by the client_plans link snapshot,
                  see clientPlanRoutes.ts) - having it here implied brokers
                  could fix a wrong area by just switching it, when the real
                  fix is "Import from Library". Replaced with a plain note:
                  which area these rates are for, and their effective date
                  (a point in time, not a range). */}
              {rateSourceBadges}
              {/* UX: age-based plans can have 50+ rows (e.g. all_ages
                  banding), so "Save Age-Based Rates" sat far below the fold -
                  easy to miss after editing. Bounding this table to its own
                  scroll area (native overflow-y-auto, no JS) keeps Save
                  always in view right below it instead of at the bottom of a
                  long page scroll; the sticky header keeps column labels
                  visible while scrolling through rows. */}
              <div className="max-h-[420px] overflow-y-auto rounded-md border">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead>Age Range</TableHead>
                    <TableHead>Monthly Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Bug (see handleAgeBandTypeChange above): apiAgeRows is
                    // whatever's actually SAVED, which can be a different
                    // structure than the one currently selected - e.g. saved
                    // under "ten_year" while "all_ages" is picked in the
                    // dropdown (exactly the mismatch reported live: dropdown
                    // said "All Ages", table showed 10-year labels). Only
                    // trust apiAgeRows when its OWN structure matches what's
                    // selected now; otherwise show the freshly-generated
                    // backendAgeRows for the newly picked structure instead.
                    const apiStructureMatches =
                      planRatesData?.rateStructure?.ageBandType === ageBandType;
                    const rowsToRender =
                      // Highest priority: API-provided rows (exact bands),
                      // but only when they're for the structure we're on
                      apiStructureMatches && apiAgeRows && apiAgeRows.length > 0
                        ? apiAgeRows
                        : backendAgeRows.length > 0
                        ? backendAgeRows
                        : lockToBackendStructure
                        ? Object.keys(ageBandRates)
                            .map((age) => ({
                              age,
                              rate: ageBandRates[age] || "",
                            }))
                            .sort(
                              (a, b) => parseStart(a.age) - parseStart(b.age)
                            )
                        : [];

                    if (rowsToRender.length > 0) {
                      // filter out any null/undefined/empty labels and ensure unique keys
                      const cleaned = rowsToRender.filter(
                        (r) => r.age != null && String(r.age).length > 0
                      );
                      const seen = new Set<string>();
                      const deduped = cleaned.filter((r) => {
                        const k = String(r.age);
                        if (seen.has(k)) return false;
                        seen.add(k);
                        return true;
                      });
                      return deduped.map(({ age, rate }) => (
                        <TableRow key={`age:${age}`}>
                          <TableCell>{normalizeDisplayLabel(age)}</TableCell>
                          <TableCell>
                            <Input
                              disabled={ratesReadOnly || ageBandSaveBlocked || !structureUnlocked}
                              type="number"
                              value={ageBandRates[age] ?? rate}
                              onChange={(e) =>
                                setAgeBandRates((prev) => ({
                                  ...prev,
                                  [age]: e.target.value,
                                }))
                              }
                              step="0.01"
                              className="w-24"
                            />
                          </TableCell>
                        </TableRow>
                      ));
                    }
                    return (
                      <>
                        {/* existing static lists as fallback */}
                        {[
                          { age: "0-14", rate: "200.00" },
                          { age: "15", rate: "225.00" },
                          { age: "16", rate: "235.00" },
                          { age: "17", rate: "245.00" },
                          { age: "18", rate: "255.00" },
                          { age: "19", rate: "265.00" },
                          { age: "20", rate: "275.00" },
                          { age: "21", rate: "285.00" },
                          { age: "22", rate: "295.00" },
                          { age: "23", rate: "305.00" },
                          { age: "24", rate: "315.00" },
                          { age: "25", rate: "325.00" },
                          { age: "26", rate: "335.00" },
                          { age: "27", rate: "345.00" },
                          { age: "28", rate: "355.00" },
                          { age: "29", rate: "365.00" },
                          { age: "30", rate: "375.00" },
                          { age: "31", rate: "380.00" },
                          { age: "32", rate: "385.00" },
                          { age: "33", rate: "390.00" },
                          { age: "34", rate: "395.00" },
                          { age: "35", rate: "400.00" },
                          { age: "36", rate: "405.00" },
                          { age: "37", rate: "410.00" },
                          { age: "38", rate: "415.00" },
                          { age: "39", rate: "420.00" },
                          { age: "40", rate: "450.00" },
                          { age: "41", rate: "460.00" },
                          { age: "42", rate: "470.00" },
                          { age: "43", rate: "480.00" },
                          { age: "44", rate: "490.00" },
                          { age: "45", rate: "500.00" },
                          { age: "46", rate: "510.00" },
                          { age: "47", rate: "520.00" },
                          { age: "48", rate: "530.00" },
                          { age: "49", rate: "540.00" },
                          { age: "50", rate: "550.00" },
                          { age: "51", rate: "560.00" },
                          { age: "52", rate: "570.00" },
                          { age: "53", rate: "580.00" },
                          { age: "54", rate: "590.00" },
                          { age: "55", rate: "600.00" },
                          { age: "56", rate: "610.00" },
                          { age: "57", rate: "620.00" },
                          { age: "58", rate: "630.00" },
                          { age: "59", rate: "640.00" },
                          { age: "60", rate: "650.00" },
                          { age: "61", rate: "660.00" },
                          { age: "62", rate: "670.00" },
                          { age: "63", rate: "680.00" },
                          { age: "64+", rate: "700.00" },
                        ].map(({ age, rate }) => (
                          <TableRow key={age}>
                            <TableCell>{age}</TableCell>
                            <TableCell>
                              <Input
                              disabled={ratesReadOnly || ageBandSaveBlocked || !structureUnlocked}
                                type="number"
                                value={ageBandRates[age] || rate}
                                onChange={(e) =>
                                  setAgeBandRates((prev) => ({
                                    ...prev,
                                    [age]: e.target.value,
                                  }))
                                }
                                step="0.01"
                                className="w-24"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    );
                  })()}
                </TableBody>
              </Table>
              </div>
            </div>

            {/* Save Button for Age-Based Rates */}
            <div className="flex flex-col items-end gap-2 pt-4 border-t">
              {ageBandSaveBlocked && (
                <p className="text-xs text-muted-foreground">
                  This plan has rates for multiple rating areas. Editing values directly here is disabled - use "Update rates" above to change a rate area's numbers instead.
                </p>
              )}
              <Button
                onClick={handleSaveAgeBandRates}
                // Bug: this ANDed in ageBandSaveBlocked unconditionally, so
                // an Age Determination-only change (which has nothing to do
                // with rate values) stayed stuck disabled on a locked
                // multi-area plan. The per-cell rate inputs already enforce
                // that guardrail on their own (disabled={... ||
                // ageBandSaveBlocked} above) - ageBandRatesDirty can never
                // actually be true while ageBandSaveBlocked is true, since
                // those same inputs are what would need to change it. Only
                // block the button for that reason when a rate value is
                // what's actually pending.
                disabled={
                  ratesReadOnly ||
                  saveAgeBandMutation.isPending ||
                  (!ageBandRatesDirty && !ageBandMetaDirty) ||
                  (ageBandRatesDirty && ageBandSaveBlocked) ||
                  !structureUnlocked
                }
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saveAgeBandMutation.isPending
                  ? "Saving..."
                  : "Save Age-Based Rates"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLifeDisability && ratingMethod === "age_tier" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Age Band + Tier Rate Matrix
            </CardTitle>
            <CardDescription>
              Define rates for each age band and coverage tier combination
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {rateSourceBadges}
            <div className="space-y-2">
              <Label className="font-medium">Age Band Structure</Label>
              <Select value={ageTierBandType} onValueChange={(val) => {
                setAgeTierBandType(val);
                setAgeTierRates({}); // reset matrix when band type changes
              }}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select age band structure" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="five_year">{AGE_BAND_TYPE_LABELS.five_year}</SelectItem>
                  <SelectItem value="ten_year">{AGE_BAND_TYPE_LABELS.ten_year}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Age Band</TableHead>
                    {COVERAGE_TIERS.map((t) => (
                      <TableHead key={t.key} className="text-center">{t.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ageTierBands.map((band) => (
                    <TableRow key={band}>
                      <TableCell className="font-medium">{band}</TableCell>
                      {COVERAGE_TIERS.map((t) => (
                        <TableCell key={t.key}>
                          <Input
                              disabled={ratesReadOnly || !structureUnlocked}
                            type="number"
                            step="0.01"
                            className="w-24 mx-auto"
                            value={ageTierRates[band]?.[t.key] ?? ""}
                            onChange={(e) =>
                              setAgeTierRates((prev) => ({
                                ...prev,
                                [band]: {
                                  ...prev[band],
                                  [t.key]: e.target.value,
                                },
                              }))
                            }
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button
                onClick={handleSaveAgeTierRates}
                disabled={ratesReadOnly || saveAgeTierMutation.isPending || !structureUnlocked}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saveAgeTierMutation.isPending ? "Saving..." : "Save Age-Tier Rates"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLifeDisability && ratingMethod === "coverage_table" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Coverage Table Rates
            </CardTitle>
            <CardDescription>
              Define rates for each coverage option
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {rateSourceBadges}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {TABLE_RATE_LABELS.map((label) => (
                <div key={label} className="space-y-1">
                  <Label>{label}</Label>
                  <Input
                              disabled={ratesReadOnly || !structureUnlocked}
                    value={(ageBandRates as any)[label] || ""}
                    onChange={(e) =>
                      setAgeBandRates((prev) => ({
                        ...prev,
                        [label]: e.target.value,
                      }))
                    }
                    placeholder="0.00"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                disabled={ratesReadOnly || !structureUnlocked}
                onClick={() => {
                  const payload = {
                    clientId: clientId ? parseInt(clientId) : null,
                    coverageTableRates: ageBandRates,
                  };
                  apiRequest(
                    "PUT",
                    `/api/plan-coverage-table-rates/bulk/${planId}`,
                    payload
                  )
                    .then(() => {
                      queryClient.setQueryData([`/api/plans/${planId}`], (prev: any) => {
                        if (!prev || typeof prev !== "object") return prev;
                        return { ...prev, needsRateReview: false };
                      });

                      queryClient.invalidateQueries({
                        queryKey: ["/api/plan-rates"],
                        refetchType: "active",
                      });
                      queryClient.invalidateQueries({
                        queryKey: ["/api/plan-rates/"],
                        refetchType: "active",
                      });
                      queryClient.invalidateQueries({
                        queryKey: ["/api/plan-rates", planId],
                        refetchType: "active",
                      });
                      queryClient.invalidateQueries({
                        queryKey: ["/api/plans"],
                        refetchType: "active",
                      });
                      queryClient.invalidateQueries({
                        queryKey: [`/api/plans/${planId}`],
                        refetchType: "active",
                      });
                      const clientIdNum = clientId ? Number(clientId) : NaN;
                      if (Number.isFinite(clientIdNum)) {
                        queryClient.invalidateQueries({
                          queryKey: ["/api/client-plans", clientIdNum],
                          refetchType: "active",
                        });
                      }
                      // Notify listeners that rates were saved
                      window.dispatchEvent(new CustomEvent("rates:saved"));
                      toast({
                        title: "Success",
                        description: "Coverage table rates saved successfully",
                      });
                    })
                    .catch((error: any) => {
                      toast({
                        title: "Error",
                        description:
                          error?.message ||
                          "Failed to save coverage table rates",
                        variant: "destructive",
                      });
                    });
                }}
              >
                Save Coverage Table Rates
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tier Rating Tab */}
      {!isLifeDisability && ratingMethod === "tier" && (
        <Card>
          <CardHeader>
            <CardTitle>Tier Rating Structure</CardTitle>
            <CardDescription>
              Configure rating tiers and their corresponding monthly rates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tier</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Monthly Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    {
                      tier: "Tier 1",
                      description: "Basic Coverage",
                      rate: "350.00",
                    },
                    {
                      tier: "Tier 2",
                      description: "Standard Coverage",
                      rate: "450.00",
                    },
                    {
                      tier: "Tier 3",
                      description: "Premium Coverage",
                      rate: "650.00",
                    },
                  ].map(({ tier, description, rate }) => (
                    <TableRow key={tier}>
                      <TableCell>{tier}</TableCell>
                      <TableCell>{description}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          defaultValue={rate}
                          step="0.01"
                          className="w-24"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </div>
  );
}
